import { ComponentType, lazy } from 'react';

/**
 * Insights post registry.
 *
 * Each post is a TSX module under `src/content/insights/` that default-exports
 * the post body React component. Frontmatter (title, date, author, tag, excerpt)
 * lives here in the registry — explicit + typed, no markdown library dependency
 * needed (avoiding a tooling-principles.md carveout for a small infrastructure
 * Story).
 *
 * Story #7867 — full content migration from conscierra.au WordPress — may
 * later convert this pattern to markdown-loaded posts with frontmatter. The
 * registry shape stays compatible.
 */
export interface InsightPost {
  /** URL slug under /insights/ (no leading slash). */
  slug: string;
  /** Page H1 + listing card title. */
  title: string;
  /** Short summary for the listing card + meta description (1-2 sentences). */
  excerpt: string;
  /** ISO 8601 date (YYYY-MM-DD) the post was first published. */
  date: string;
  /** Display author name. */
  author: string;
  /** Categorical tag pills for the listing card (1-3 tags). */
  tags: readonly string[];
  /** Lazy-loaded post body component (default export of the module). */
  Component: ComponentType;
}

export const insightPosts: readonly InsightPost[] = [
  {
    slug: 'saas-sprawl-tax-why-businesses-pay-20-200k-per-year',
    title: 'The SaaS sprawl tax: why growing businesses pay $20–$200K/year for fragmentation',
    excerpt:
      "Most growing businesses run on 5–15 SaaS subscriptions that don't talk to each other. We break down where the real annual cost sits — and why adding more tools usually makes it worse, not better.",
    date: '2026-05-14',
    author: 'Stephen Betts',
    tags: ['SaaS', 'Platform Builds', 'Cost'],
    Component: lazy(() => import('./posts/saas-sprawl-tax')),
  },
  {
    slug: 'microsoft-365-e7-frontier-ai-explainer',
    title: 'Microsoft 365 E7 (Frontier) — what changes for AI in your business',
    excerpt:
      "Microsoft launched E7 on 1 May 2026, bundling Copilot, Agent 365, Work IQ and advanced Defender / Purview at $99/user/month. Here's what that actually changes — and what it doesn't.",
    date: '2026-05-12',
    author: 'Stephen Betts',
    tags: ['Microsoft 365', 'AI', 'Copilot'],
    Component: lazy(() => import('./posts/microsoft-e7-frontier-ai')),
  },
] as const;

/** Lookup by slug. Returns undefined when not found. */
export function findPostBySlug(slug: string): InsightPost | undefined {
  return insightPosts.find((p) => p.slug === slug);
}

/** Posts sorted newest first by `date`. */
export function postsByDateDesc(): readonly InsightPost[] {
  return [...insightPosts].sort((a, b) => b.date.localeCompare(a.date));
}
